<script lang="ts">
  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { session } from '../lib/session.svelte';
  import type { AdminUser, PublicSettings } from '../../../shared/types';

  interface Props {
    onDone: () => void;
  }
  let { onDone }: Props = $props();

  let users: AdminUser[] = $state([]);
  let loading = $state(true);
  let error: string | null = $state(null);

  let settings: PublicSettings | null = $state(null);

  onMount(async () => {
    await load();
    try {
      settings = await api.getSettings();
    } catch {
      // Settings load failure is non-fatal; the toggle just won't render.
    }
  });

  async function load() {
    loading = true;
    error = null;
    try {
      users = await api.adminListUsers();
    } catch (err) {
      error = err instanceof ApiError && err.status === 403
        ? 'Bare administratorer kan se denne siden.'
        : 'Kunne ikke laste brukere.';
    } finally {
      loading = false;
    }
  }

  async function toggleSelfRegistry(next: boolean) {
    const prev = settings;
    settings = settings ? { ...settings, self_registry_enabled: next } : settings;
    try {
      settings = await api.updateSettings({ self_registry_enabled: next });
    } catch {
      settings = prev;
      error = 'Kunne ikke endre innstilling.';
    }
  }

  /**
   * Optimistic update: flip the role locally, then send the PATCH. If the
   * server rejects (e.g., last-admin guard fires), reload from the server so
   * the UI reflects the actual state.
   */
  async function setRole(user: AdminUser, change: { is_moderator?: boolean; is_admin?: boolean }) {
    try {
      const updated = await api.adminSetRole(user.id, change);
      users = users.map((u) => (u.id === updated.id ? updated : u));
      // If we just toggled OUR OWN role, refresh the in-memory session so the
      // nav links (Admin, Tilbakemelding) reflect the new state immediately.
      if (session.user && updated.id === session.user.id) {
        session.user.is_moderator = updated.is_moderator;
        session.user.is_admin = updated.is_admin;
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        error = 'Kan ikke fjerne den siste administratoren.';
      } else {
        error = 'Endringen feilet.';
      }
      // Reload to resync state.
      await load();
    }
  }

  function formatDate(epochMs: number): string {
    return new Date(epochMs).toLocaleDateString('nb-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  }
</script>

<section aria-label="Administrasjon">
  <div class="row" style="justify-content: space-between; margin-bottom: 1rem;">
    <h2 style="margin: 0;">Administrasjon</h2>
    <button type="button" onclick={onDone}>Tilbake</button>
  </div>

  <p class="muted">
    Administratorer kan utnevne moderatorer og andre administratorer. Du kan
    ikke fjerne den siste administratoren — bruk <code>sqlite3</code> direkte
    om du må.
  </p>

  {#if settings}
    <div class="card">
      <h3 style="margin-top: 0;">Selvregistrering</h3>
      <label class="row" style="gap: 0.5rem; align-items: center;">
        <input
          type="checkbox"
          checked={settings.self_registry_enabled}
          onchange={(e) => toggleSelfRegistry((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>
          {settings.self_registry_enabled
            ? 'Åpen — hvem som helst kan opprette konto.'
            : 'Lukket — bare invitasjonslenker fungerer.'}
        </span>
      </label>
    </div>
  {/if}

  {#if error}<p class="error" role="alert">{error}</p>{/if}

  {#if loading}
    <p class="muted">Laster …</p>
  {:else}
    <div class="card" style="padding: 0; overflow-x: auto;">
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="border-bottom: 1px solid var(--border);">
            <th scope="col" style="text-align: left; padding: 0.5rem;">Bruker</th>
            <th scope="col" style="text-align: left; padding: 0.5rem;">Opprettet</th>
            <th scope="col" style="text-align: left; padding: 0.5rem;">Moderator</th>
            <th scope="col" style="text-align: left; padding: 0.5rem;">Admin</th>
          </tr>
        </thead>
        <tbody>
          {#each users as u (u.id)}
            <tr style="border-bottom: 1px solid var(--border);">
              <td style="padding: 0.5rem;">
                <div>{u.display_name?.trim() || u.email}</div>
                <div class="muted" style="font-size: 0.8rem;">{u.email}{u.username ? ` · @${u.username}` : ''}</div>
              </td>
              <td style="padding: 0.5rem;" class="muted">{formatDate(u.created_at)}</td>
              <td style="padding: 0.5rem;">
                <label class="row" style="gap: 0.4rem;">
                  <input
                    type="checkbox"
                    checked={u.is_moderator}
                    onchange={(e) => setRole(u, { is_moderator: (e.currentTarget as HTMLInputElement).checked })}
                  />
                  <span class="muted">{u.is_moderator ? 'Ja' : 'Nei'}</span>
                </label>
              </td>
              <td style="padding: 0.5rem;">
                <label class="row" style="gap: 0.4rem;">
                  <input
                    type="checkbox"
                    checked={u.is_admin}
                    onchange={(e) => setRole(u, { is_admin: (e.currentTarget as HTMLInputElement).checked })}
                  />
                  <span class="muted">{u.is_admin ? 'Ja' : 'Nei'}</span>
                </label>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>
